import { useToast } from "@chakra-ui/react";
import { getEnv } from "mobx-easy";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { RootEnv } from "stores/setup/create-store";
import { useStores } from "stores/setup/use-store";
import { CreateEventFormValues } from "../types";
import {
    mapFormValuesToEventPayload,
    validateEventPayload,
} from "../utils/eventPayloadMapper";

interface UseEventCreationSubmissionParams {
    onSuccessCallback?: () => void;
    showPhoneVerificationModal?: boolean;
}

/**
 * useEventCreationSubmission - Handles event creation submission
 * 
 * Uses the unified /v1/event endpoint that handles both Insight and Alert
 * creation in a single request. The backend manages atomicity.
 * 
 * Control flags:
 * - shouldCreateInsight (block1Active): Creates insight if true
 * - shouldCreateAlert (block2Active): Creates alert if true
 */
export function useEventCreationSubmission({
    onSuccessCallback,
    showPhoneVerificationModal,
}: UseEventCreationSubmissionParams = {}) {
    const {
        dataStores: { insightAlertStore, oceanWeatherIntelligenceStore },
    } = useStores();

    const { eventService } = getEnv<RootEnv>();

    const toast = useToast();
    const { t } = useTranslation("translation", {
        keyPrefix: "event",
    });

    // Separate translator for validation messages (no keyPrefix)
    const { t: tValidation } = useTranslation("translation");

    const [isSubmitting, setIsSubmitting] = useState(false);

    /**
     * Translates validation error keys to localized messages
     */
    const translateValidationErrors = useCallback((errors: string[]): string => {
        return errors.map(errorKey => tValidation(errorKey, { defaultValue: errorKey })).join(", ");
    }, [tValidation]);

    /**
     * Creates Event using the unified endpoint
     * Sends single request to POST /v1/event
     */
    const createEvent = useCallback(
        async (values: CreateEventFormValues): Promise<boolean> => {
            try {
                // Map form values to unified payload
                // Pass all store values needed for location resolution
                const payload = mapFormValuesToEventPayload(
                    values,
                    values.selectedUsers || [],
                    insightAlertStore.macroRegionOrigin ?? undefined,
                    insightAlertStore.selectedGeofence ?? undefined,
                    insightAlertStore.selectedStationForAlert, // Pass selected station for custom point detection
                    insightAlertStore.isGeofenceSavedToLocation // Pass flag for loose area detection
                );

                // Validate payload before sending
                const validation = validateEventPayload(payload);
                if (!validation.isValid) {
                    toast({
                        title: t("validationError"),
                        description: translateValidationErrors(validation.errors),
                        status: "error",
                        position: "bottom-right",
                        isClosable: true,
                    });
                    return false;
                }

                // Call the unified endpoint
                const response = await eventService.createEvent(payload);

                if (response.success) {
                    return true;
                } else {
                    toast({
                        title: t("errorCreatingEvent"),
                        description: response.message || undefined,
                        status: "error",
                        position: "bottom-right",
                        isClosable: true,
                    });
                    return false;
                }
            } catch (error) {
                toast({
                    title: t("errorCreatingEvent"),
                    description: error instanceof Error ? error.message : undefined,
                    status: "error",
                    position: "bottom-right",
                    isClosable: true,
                });
                return false;
            }
        },
        [
            eventService,
            insightAlertStore.macroRegionOrigin,
            insightAlertStore.selectedGeofence,
            insightAlertStore.selectedStationForAlert,
            toast,
            t,
            translateValidationErrors,
        ]
    );

    /**
     * Main submission handler
     * Sends single request to /v1/event endpoint
     */
    const handleEventSubmission = useCallback(
        async (values: CreateEventFormValues, onClose: () => void) => {
            setIsSubmitting(true);

            try {
                const success = await createEvent(values);

                if (success) {
                    // Update Gantt chart to reflect new event
                    oceanWeatherIntelligenceStore.setChangeForUpdate({
                        update: true,
                        expiration: 0,
                    });

                    toast({
                        title: t("successEventCreated"),
                        status: "success",
                        position: "bottom-right",
                        isClosable: true,
                    });

                    onClose();

                    // Trigger phone verification modal if needed
                    if (onSuccessCallback && showPhoneVerificationModal && values.block2Active) {
                        setTimeout(() => {
                            onSuccessCallback();
                        }, 500);
                    }
                }
            } catch (error) {
                toast({
                    title: t("errorCreatingEvent"),
                    description: error instanceof Error ? error.message : undefined,
                    status: "error",
                    position: "bottom-right",
                    isClosable: true,
                });
            } finally {
                setIsSubmitting(false);
            }
        },
        [createEvent, toast, t, onSuccessCallback, showPhoneVerificationModal, oceanWeatherIntelligenceStore]
    );

    return {
        handleEventSubmission,
        isSubmitting,
        setIsSubmitting,
    };
}
